import { fetchApi } from "~/lib/api";
import type {
  RedeemCodeRequest,
  ShareCodeResponse,
  ShareGiftEmailRequest,
  SharedSongRecipient,
} from "~/types";

export const songSharingService = {
  /**
   * Get or lazily generate share URL for an order
   */
  async getShareUrl(orderId: number): Promise<ShareCodeResponse> {
    return fetchApi<ShareCodeResponse>(
      `/customer/song-sharing/${orderId}/share-url`,
    );
  },

  /**
   * Send gift email to recipient
   */
  async sendGiftEmail(
    orderId: number,
    recipientEmail: string,
  ): Promise<{ success: boolean; message: string }> {
    return fetchApi<{ success: boolean; message: string }>(
      `/customer/song-sharing/${orderId}/send-email`,
      {
        method: "POST",
        body: JSON.stringify({ recipientEmail } as ShareGiftEmailRequest),
      },
    );
  },

  /**
   * Redeem a share code (authenticated)
   */
  async redeemCode(
    shareCode: string,
  ): Promise<{ success: boolean; message: string }> {
    return fetchApi<{ success: boolean; message: string }>(
      "/customer/song-sharing/redeem",
      {
        method: "POST",
        body: JSON.stringify({ shareCode } as RedeemCodeRequest),
      },
    );
  },

  /**
   * Get list of recipients for an order
   */
  async getRecipients(orderId: number): Promise<SharedSongRecipient[]> {
    return fetchApi<SharedSongRecipient[]>(
      `/customer/song-sharing/${orderId}/recipients`,
    );
  },
  /**
   * Submit a review for a shared order (accessible to owner or redeemed recipient)
   */
  async submitReview(
    orderId: number,
    review: { rating: number; note?: string },
  ) {
    return fetchApi<void>(`/customer/song-sharing/order/${orderId}/review`, {
      method: "POST",
      body: JSON.stringify(review),
    });
  },
};

/**
 * Anonymous service for gift preview (no auth required)
 */
export const anonymousSongGiftService = {
  /**
   * Get gift preview by share code
   */
  async getGiftPreview(shareCode: string): Promise<{
    childName: string;
    songName: string;
    hasArtwork: boolean;
    isAvailable: boolean;
  }> {
    return fetchApi<{
      childName: string;
      songName: string;
      hasArtwork: boolean;
      isAvailable: boolean;
    }>(`/anonymous/song-gift/${shareCode}`);
  },
};
